import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheckCircle, HiClock, HiRefresh, HiExclamationCircle } from 'react-icons/hi';

import { Button } from '@/components/common';
import CuttingFormLineManager, { CuttingFormLineManagerRef } from './CuttingFormLineManager';
import { cuttingApi } from '@/api/endpoints/cutting';
import { toast } from '@/store/toastStore';

import {
  CreateCuttingFormRequest,
  UpdateCuttingFormRequest,
  CuttingFormResponse,
  CuttingFormStatus
} from '@/types/cutting';

interface CuttingOrderFormProps {
  cuttingOrder?: CuttingFormResponse;
  onSubmit: (data: CreateCuttingFormRequest | UpdateCuttingFormRequest) => void;
  onCancel: () => void;
  onSaveAndClose?: (data: CreateCuttingFormRequest | UpdateCuttingFormRequest) => void;
  loading?: boolean;
  onFormReset?: () => void; // Callback to notify parent that form should be reset
  onStatusTransition?: (status: CuttingFormStatus) => void; // Callback for status transitions
  isViewOnly?: boolean;
}

export interface CuttingOrderFormRef {
  resetForm: () => void;
}

const CuttingOrderForm = forwardRef<CuttingOrderFormRef, CuttingOrderFormProps>(({
  cuttingOrder,
  onSubmit,
  onCancel,
  onSaveAndClose,
  loading = false,
  onFormReset,
  onStatusTransition,
  isViewOnly = false,
}, ref) => {
  const { t } = useTranslation();
  const isEditing = !!cuttingOrder;

  const [formData, setFormData] = useState<CreateCuttingFormRequest>({
    note: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [cuttingFormLines, setCuttingFormLines] = useState<any[]>([]);
  const [initialLines, setInitialLines] = useState<any[]>([]);

  const [loadingLines, setLoadingLines] = useState(false);
  const [statusTransitionLoading, setStatusTransitionLoading] = useState<string | null>(null);
  const cuttingFormLineManagerRef = useRef<CuttingFormLineManagerRef>(null);

  // Status management functions
  const getAvailableStatusTransitions = (currentStatus: CuttingFormStatus): CuttingFormStatus[] => {
    switch (currentStatus) {
      case 'created':
        return ['cut'];
      case 'cut':
        return ['confirmed', 'rechecked'];
      case 'rechecked':
        return ['confirmed'];
      case 'confirmed':
      default:
        return [];
    }
  };

  const getStatusTransitionButton = (status: CuttingFormStatus) => {
    const statusConfig: Partial<Record<CuttingFormStatus, {
      label: string;
      icon: React.ReactElement;
      variant: 'primary' | 'outline';
      color: string;
    }>> = {
      cut: {
        label: t('cuttingOrders.statusTransitions.cut'),
        icon: <HiCheckCircle className="h-4 w-4" />,
        variant: 'primary' as const,
        color: 'bg-blue-600 hover:bg-blue-700',
      },
      confirmed: {
        label: t('cuttingOrders.statusTransitions.confirm'),
        icon: <HiClock className="h-4 w-4" />,
        variant: 'primary' as const,
        color: 'bg-green-600 hover:bg-green-700',
      },
      rechecked: {
        label: t('cuttingOrders.statusTransitions.recheck'),
        icon: <HiRefresh className="h-4 w-4" />,
        variant: 'primary' as const,
        color: 'bg-orange-600 hover:bg-orange-700',
      },
    };

    return statusConfig[status] || null;
  };

  const handleStatusTransition = async (newStatus: CuttingFormStatus) => {
    if (!cuttingOrder || !onStatusTransition) return;

    const confirmMessage = t('cuttingOrders.statusTransitions.confirmMessage', {
      status: t(`cuttingOrders.status.${newStatus}`),
      code: cuttingOrder.code,
    });

    if (!window.confirm(confirmMessage)) return;

    try {
      setStatusTransitionLoading(newStatus);

      // Call the appropriate API method based on the status
      switch (newStatus) {
        case 'cut':
          await cuttingApi.cutCuttingForm(cuttingOrder.code);
          break;
        case 'confirmed':
          await cuttingApi.confirmCuttingForm(cuttingOrder.code);
          break;
        case 'rechecked':
          await cuttingApi.recheckCuttingForm(cuttingOrder.code);
          break;
        default:
          await cuttingApi.updateCuttingFormStatus(cuttingOrder.code, newStatus);
      }

      onStatusTransition(newStatus);
    } catch (error) {
      console.error('Error transitioning status:', error);
      alert(t('cuttingOrders.messages.statusUpdateError'));
    } finally {
      setStatusTransitionLoading(null);
    }
  };

  // Check if form fields should be editable based on status
  const isFormEditable = () => {
    if (!isEditing) return true; // New forms are always editable
    if (cuttingOrder?.status === 'created') return true;
    return false;
  };

  // Check if note field should be editable based on status
  const isNoteEditable = () => {
    if (!isEditing) return true;
    return ['created', 'cut'].includes(cuttingOrder?.status || '');
  };

  // Check if actual quantity fields should be editable based on status
  const isActualQtyEditable = () => {
    return isEditing && cuttingOrder?.status === 'rechecked';
  };

  // Check if nested table note fields should be editable based on status
  const isNestedTableNoteEditable = () => {
    return ['rechecked'].includes(cuttingOrder?.status || '');
  };

  // Check if nested table should be expandable based on status
  const isNestedTableExpandable = () => {
    return ['cut', 'rechecked','confirmed'].includes(cuttingOrder?.status || '');
  };

  // Load cutting form lines when editing
  const loadCuttingFormLines = async (cuttingFormCode: string) => {
    console.log('🚀 Loading cutting form lines for code:', cuttingFormCode);
    setLoadingLines(true);
    try {
      const lines = await cuttingApi.getCuttingFormLines(cuttingFormCode);
      console.log('📦 Received cutting form lines:', lines);
      setInitialLines(lines);


    } catch (error) {
      console.error('❌ Error loading cutting form lines:', error);
      setInitialLines([]);
    } finally {
      setLoadingLines(false);
    }
  };



  // Initialize form data when cutting form prop changes
  useEffect(() => {
    if (cuttingOrder) {
      setFormData({
        note: cuttingOrder.note || '',
      });

      // Load cutting form lines only if they haven't been loaded yet
      if (initialLines.length === 0) {
        loadCuttingFormLines(cuttingOrder.code);
      }
    } else {
      setInitialLines([]);
    }
  }, [cuttingOrder]);

  // Handle add/delete/update operations for cutting form lines
  const handleCuttingFormLinesOperations = async () => {
    if (!cuttingOrder) return;

    try {
      const newLines = cuttingFormLines.filter(line => line.isNew && !line.isDeleted);
      const modifiedLines = cuttingFormLines.filter(line => line.isModified && !line.isDeleted && !line.isNew);
      const deletedLines = cuttingFormLines.filter(line => line.isDeleted && !line.isNew);

      console.log(`📊 Processing: ${newLines.length} new, ${modifiedLines.length} modified, ${deletedLines.length} deleted lines`);

      // Process each line based on its state
      for (const line of cuttingFormLines) {
        if (line.isNew && !line.isDeleted) {
          // Add new line
          await handleAddCuttingFormLine(line);
        } else if (line.isModified && !line.isDeleted && !line.isNew) {
          // Update existing line
          await handleUpdateCuttingFormLine(line);
        } else if (line.isDeleted && !line.isNew) {
          // Delete existing line
          await handleDeleteCuttingFormLine(line);
        }
      }
    } catch (error) {
      console.error('Error processing cutting form lines:', error);
      // Show specific error message from API if available
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast.error(t('cuttingOrders.messages.processLinesError', { error: errorMessage }));
      throw error; // Re-throw to prevent form submission
    }
  };

  // Add a new cutting form line
  const handleAddCuttingFormLine = async (line: any) => {
    const cuttingFormLineData = {
      cutting_form_id: cuttingOrder!.code,
      raw_material_id: line.raw_material_id || line.materialId,
      cutting_config_id: line.cutting_config_id || line.cuttingConfigId,
      cutting_type: line.cutting_type || line.cuttingType || 1,
      input_qty: line.input_qty || line.inputQty || 1,
    };

    await cuttingApi.createCuttingFormLine(cuttingFormLineData);
  };

  // Update an existing cutting form line
  const handleUpdateCuttingFormLine = async (line: any) => {
    if (!line.code) return;

    const updateData = {
      cutting_form_id: cuttingOrder!.code,
      raw_material_id: line.raw_material_id || line.materialId,
      cutting_config_id: line.cutting_config_id || line.cuttingConfigId,
      cutting_type: line.cutting_type || line.cuttingType || 1,
      input_qty: line.input_qty || line.inputQty || 1,
    };

    await cuttingApi.updateCuttingFormLine(line.code, updateData);
  };

  // Delete an existing cutting form line
  const handleDeleteCuttingFormLine = async (line: any) => {
    if (!line.code) return;
    console.log('🔥 Calling individual deleteCuttingFormLine API with code:', line.code);
    await cuttingApi.deleteCuttingFormLine(line.code);
    console.log('✅ Individual deleteCuttingFormLine API completed');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Note is optional for cutting forms, so no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      note: '',
    });
    setCuttingFormLines([]);
    setErrors({});

    // Reset the cutting form line manager
    if (cuttingFormLineManagerRef.current) {
      cuttingFormLineManagerRef.current.resetForm();
    }

    if (onFormReset) {
      onFormReset();
    }
  };

  // Expose resetForm function to parent component
  useImperativeHandle(ref, () => ({
    resetForm,
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
         // Handle batch update of actual quantities if in rechecked status
        if (cuttingOrder?.status === 'rechecked') {
          console.log('🔄 Processing actual quantities batch update...');
          await handleUpdateActualQuantities();
          console.log('✅ Actual quantities batch update completed');
          return
        } else {
          // Handle cutting form lines operations when updating
          console.log('[object Object] operations...');
          await handleCuttingFormLinesOperations();
          console.log('✅ Cutting form lines operations completed');
        }
      }

      // Include cutting form lines in the submission
      const submissionData = {
        ...formData,
        cuttingFormLines: cuttingFormLines.filter(line => !line.isDeleted),
      };

      if (isEditing) {
        onSubmit({ ...submissionData, id: cuttingOrder!.id } as UpdateCuttingFormRequest);
        toast.success(t('cuttingOrders.messages.updateSuccess'));
      } else {
        onSubmit(submissionData);
        toast.success(t('cuttingOrders.messages.createSuccess'));
      }
    } catch (error) {
      console.error('❌ Error during form submission:', error);
      // Show specific error message from API if available
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const baseMessage = isEditing ? t('cuttingOrders.messages.updateError') : t('cuttingOrders.messages.createError');
      toast.error(`${baseMessage}: ${errorMessage}`);
    }
  };

  const handleSaveAndClose = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
        // Handle cutting form lines operations when updating
        console.log('[object Object] operations...');
        await handleCuttingFormLinesOperations();
        console.log('✅ Cutting form lines operations completed');

        // Handle batch update of actual quantities if in rechecked status
        if (cuttingOrder?.status === 'rechecked') {
          console.log('[object Object].');
          await handleUpdateActualQuantities();
          console.log('✅ Actual quantities batch update completed');
        }
      }

      if (onSaveAndClose) {
        // Include cutting form lines in the submission
        const submissionData = {
          ...formData,
          cuttingFormLines: cuttingFormLines.filter(line => !line.isDeleted),
        };

        if (isEditing) {
          onSaveAndClose({ ...submissionData, id: cuttingOrder!.id } as UpdateCuttingFormRequest);
          toast.success(t('cuttingOrders.messages.updateAndCloseSuccess'));
        } else {
          onSaveAndClose(submissionData);
          toast.success(t('cuttingOrders.messages.saveAndCloseSuccess'));
        }
      }
    } catch (error) {
      console.error('❌ Error during save and close:', error);
      // Show specific error message from API if available
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      const baseMessage = isEditing ? t('cuttingOrders.messages.updateAndCloseError') : t('cuttingOrders.messages.saveAndCloseError');
      toast.error(`${baseMessage}: ${errorMessage}`);
    }
  };

  const handleInputChange = (field: keyof CreateCuttingFormRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Prevent form submission on Enter key press in input fields
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleUpdateActualQuantities = async () => {
    if (!cuttingOrder) {
      console.error('No cutting order found for actual quantities update');
      return;
    }

    const modifiedLines = cuttingFormLines.filter(line => line.isModified && !line.isNew);
    if (modifiedLines.length === 0) {
      console.log('No modified lines found, skipping actual quantities update');
      return;
    }

    // Validate that all modified outputs have valid data
    for (const line of modifiedLines) {
      for (const output of line.outputs) {
        if (output.actual_qty < 0) {
          const errorMessage = t('cuttingOrders.messages.invalidActualQuantity');
          alert(errorMessage);
          throw new Error(errorMessage);
        }
        if (output.note && output.note.length > 500) {
          const errorMessage = t('cuttingOrders.messages.noteTooLong');
          alert(errorMessage);
          throw new Error(errorMessage);
        }
      }
    }

    const payload = {
      lines: modifiedLines.map(line => ({
        line_id: line.code,
        outputs: line.outputs.map((o: any) => ({
          cutting_config_line_id: o.cutting_config_line_id || o.cuttingConfigLineId,
          actual_qty: o.actual_qty || 0,
          note: o.note || '',
        })),
      })),
    };

    try {
      console.log('Sending batch update for actual quantities:', payload);
      await cuttingApi.updateAllActualQuantities(cuttingOrder.code, payload);
      console.log('Batch update for actual quantities completed successfully');
      toast.success(t('cuttingOrders.messages.updateActualQtySuccess'));
    } catch (error: any) {
      console.error('Error updating actual quantities:', error);

      // Provide more specific error messages based on error type
      let errorMessage = t('cuttingOrders.messages.updateActualQtyError');

      if (error.message?.includes('not found')) {
        errorMessage = t('cuttingOrders.messages.cuttingFormNotFound');
      } else if (error.message?.includes('validation')) {
        errorMessage = t('cuttingOrders.messages.validationError');
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMessage = t('cuttingOrders.messages.networkError');
      }

      toast.error(errorMessage);
      throw error; // Re-throw to prevent form submission
    }
  };



  // Generate display values for readonly fields
  const cuttingFormId = cuttingOrder?.code || t('common.autoGenerated');
  const creator = t('common.currentUser');



  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Top Input Grid - two-column layout as specified */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Cutting Form ID (auto) */}
        <div>
          <label htmlFor="cuttingFormId" className="block text-sm font-medium text-gray-700 mb-1">
            {t('cuttingOrders.fields.code')}
          </label>
          <input
            type="text"
            id="cuttingFormId"
            value={cuttingFormId}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            placeholder={t('cuttingOrders.fields.code')}
            onKeyDown={handleKeyDown}
          />
        </div>

        {/* Right: Creator */}
        <div>
          <label htmlFor="creator" className="block text-sm font-medium text-gray-700 mb-1">
            {t('cuttingOrders.fields.createdBy')}
          </label>
          <input
            type="text"
            id="creator"
            value={creator}
            readOnly
            className="input-field bg-gray-50 text-gray-500 cursor-not-allowed"
            onKeyDown={handleKeyDown}
            placeholder={t('cuttingOrders.fields.createdBy')}
          />
        </div>
      </div>

      {/* Status Display for Editing */}
      {isEditing && cuttingOrder && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('cuttingOrders.fields.status')}
          </label>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              cuttingOrder.status === 'created' ? 'bg-gray-100 text-gray-800' :
              cuttingOrder.status === 'cut' ? 'bg-blue-100 text-blue-800' :
              cuttingOrder.status === 'confirmed' ? 'bg-green-100 text-green-800' :
              cuttingOrder.status === 'rechecked' ? 'bg-orange-100 text-orange-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {t(`cuttingOrders.status.${cuttingOrder.status}`)}
            </span>
            {!isFormEditable() && (
              <span className="text-sm text-gray-500 flex items-center">
                <HiExclamationCircle className="h-4 w-4 mr-1" />
                {t('cuttingOrders.messages.readOnlyDueToStatus')}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Note Field - full-width as specified */}
      <div>
        <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
          {t('cuttingOrders.fields.notes')}
        </label>
        <textarea
          id="note"
          rows={3}
          value={formData.note || ''}
          onChange={(e) => handleInputChange('note', e.target.value)}
          className={`input-field resize-none ${!isNoteEditable() ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
          placeholder={t('cuttingOrders.fields.notes')}
          disabled={loading || !isNoteEditable()}
        />
        {!isNoteEditable() && (
          <p className="mt-1 text-sm text-gray-500">
            {t('cuttingOrders.messages.fieldDisabledDueToStatus')}
          </p>
        )}
      </div>

      {/* Cutting Form Lines Manager */}
      <CuttingFormLineManager
        ref={cuttingFormLineManagerRef}
        cuttingFormId={cuttingOrder?.id}
        initialLines={initialLines}
        onChange={setCuttingFormLines}
        disabled={loading || loadingLines || !isFormEditable()}
        actualQtyEditable={isActualQtyEditable()}
        nestedTableNoteEditable={isNestedTableNoteEditable()}
        nestedTableExpandable={isNestedTableExpandable()}
      />



      {/* Action Buttons - Status transitions and form actions */}
      {!isViewOnly && ( <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        {/* Status Transition Buttons - Left side */}
        <div className="flex space-x-3">
          {isEditing && cuttingOrder && getAvailableStatusTransitions(cuttingOrder.status).map((status) => {
            const buttonConfig = getStatusTransitionButton(status);
            if (!buttonConfig) return null;

            return (
              <Button
                key={status}
                type="button"
                variant={buttonConfig.variant}
                onClick={() => handleStatusTransition(status)}
                loading={statusTransitionLoading === status}
                disabled={loading || statusTransitionLoading !== null}
                icon={buttonConfig.icon}
                className={buttonConfig.color}
              >
                {buttonConfig.label}
              </Button>
            );
          })}
        </div>

        {/* Form Action Buttons - Right side */}
        <div className="flex space-x-3">
          {/* Cancel/Close Button */}
          {cuttingOrder?.status !== 'confirmed' && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md font-medium"
            >
              {t('common.cancel')}
            </Button>
          )}

          {cuttingOrder?.status === 'confirmed' && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md font-medium"
            >
              {t('common.close')}
            </Button>
          )}

          {/* Save Button */}
          {((!isEditing) || ['created', 'cut', 'rechecked'].includes(cuttingOrder?.status || '')) && (
            <Button
              type="submit"
              variant="outline"
              loading={loading}
              className="px-4 py-2 border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md font-medium transition-colors duration-200"
            >
              {isEditing ? t('common.update') : t('common.save')}
            </Button>
          )}

          {/* Save and Close Button */}
          {onSaveAndClose && ((!isEditing) || ['created', 'cut'].includes(cuttingOrder?.status || '')) && (
            <Button
              type="button"
              variant="primary"
              onClick={handleSaveAndClose}
              loading={loading}
              className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md font-medium transition-colors duration-200 shadow-sm"
            >
              {isEditing ? t('common.updateAndClose') : t('common.saveAndClose')}
            </Button>
          )}
        </div>
      </div>
)}
    </form>
  );
});

export default CuttingOrderForm;
